import { Typography } from '@src/components/Typography/Typography';
import { useParticipantProgressFilter } from '../ParticipantProgressFilter';
import { createPortal } from 'react-dom';
import { DockedSheet } from '@src/components/Sheet/DockedSheet';
import { showSheet } from '@src/components/Sheet/Sheet';
import { closeDialog, subscribeDialog } from '@src/components/Dialog/Dialog';
import { OrganizerPanelActivity } from '../OrganizerPanelActivity';
import { Panel } from '@src/components/Panel/Panel';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrganizerPanelHome, type OrganizerPanelHomeProps } from './OrganizerPanelHome';

import { useMobileBoxLayout } from './BoxLayouts';

import { UsersIcon } from '@src/components/icons/UsersIcon';
import { PaperPlaneIcon } from '@src/components/icons/PaperPlaneIcon';

const tabs = ['list', 'invite'] as const;
type PeopleTab = typeof tabs[number];

export function OrganizerPeoplePanel(props: OrganizerPanelHomeProps) {
  const { t } = useTranslation();
  const mobile = useMobileBoxLayout();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const selected = params.get('tab') === 'history' || params.get('tab') === 'more' || location.hash === '#history' ? 'list' : ['invite', 'organizers'].includes(params.get('tab') ?? (location.hash === '#organizers' ? 'organizers' : '')) ? 'invite' : params.get('people');
  const active = tabs.find(tab => tab === selected) ?? 'list';
  const peopleRoot = useRef<HTMLDivElement>(null);
  const [animateDocked, setAnimateDocked] = useState(false);
  const [dockedTop, setDockedTop] = useState<number | null>(null);
  const [tableSlot, setTableSlot] = useState<HTMLDivElement | null>(null);
  const progressContext = useParticipantProgressFilter();
  const swipe = useRef({ y: 0, dragged: false });
  const openTable = useCallback((expanded = false) => {
    setAnimateDocked(false);
    showSheet({
    sheetOriginHeight: mobile && dockedTop !== null ? window.innerHeight - dockedTop : undefined,
    sheetInitiallyExpanded: expanded,
    title: `${t('box.organizer.filters.title')} (${props.info.participantsNumber})`, testId: 'participants-table-sheet',
    children: <div className="participants-table-sheet" ref={setTableSlot} />,
    });
  }, [t, props.info.participantsNumber, mobile, dockedTop]);
  useEffect(() => {
    if (active !== 'list') return;
    let ownSheetOnTop = false;
    const unsubscribe = subscribeDialog(dialog => { ownSheetOnTop = dialog?.testId === 'participants-table-sheet'; });

    return () => {

      unsubscribe();
      if (ownSheetOnTop) closeDialog();
      setTableSlot(null);
    };
  }, [active, props.info.box.key]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronize the external drawer and measured history layout with navigation.
    if (mobile && active === 'list' && (progressContext?.filter || typeof location.state?.previewCardId === 'number') && !tableSlot) openTable(typeof location.state?.previewCardId === 'number');
  }, [progressContext?.filter, location.state?.previewCardId]);
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronize the external drawer and measured history layout with navigation.
    if (!mobile || active !== 'list') { setDockedTop(null); return; }
    const history = peopleRoot.current?.querySelector<HTMLDetailsElement>('#history');
    if (!history) return;
    let wasOpen = history.open;
    const update = () => {
      if (wasOpen && !history.open) setAnimateDocked(true);
      wasOpen = history.open;
      setDockedTop(history.open ? null : Math.max(12, Math.min(window.innerHeight - 100, history.getBoundingClientRect().bottom + 8)));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(history);
    history.addEventListener('toggle', update);
    document.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { observer.disconnect(); history.removeEventListener('toggle', update); document.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [mobile, active, props.info.box.key]);
  const table = <OrganizerPanelHome {...props} inDialog={Boolean(tableSlot)} inlineMobile={false} embedded section={active === 'list' ? 'users' : 'invite'} />;


  function select(tab: PeopleTab) {
    if (tab === 'list' && active === 'list') {
      openTable(true);
      return;
    }
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', 'users');
      if (tab === 'list') next.delete('people');
      else next.set('people', tab);
      return next;
    }, { preventScrollReset: true });
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    buttons.current[next]?.focus();
    select(tabs[next]);
  }

  return <div ref={peopleRoot} className="organizer-people organizer-people--nested" data-testid="organizer-people">
    {active === 'list' && mobile && dockedTop === null && !tableSlot && <button type="button" className="participants-drawer-launcher" data-testid="participants-drawer-launcher" aria-haspopup="dialog"
      onPointerDown={event => { swipe.current = { y: event.clientY, dragged: false }; event.currentTarget.setPointerCapture(event.pointerId); }}
      onPointerUp={event => { if (swipe.current.y - event.clientY > 24) { swipe.current.dragged = true; openTable(true); } }}
      onPointerCancel={() => { swipe.current.dragged = false; }}
      onClick={() => { if (!swipe.current.dragged) openTable(true); swipe.current.dragged = false; }}>
      <span className="participants-drawer-launcher__handle" aria-hidden="true" />
      <span><UsersIcon /><Typography as="span" role="title/sub">{t('box.organizer.filters.title')} ({props.info.participantsNumber})</Typography></span>
    </button>}
    <div className="organizer-people__tabs" role="tablist" aria-label={t('organizerPanel.peopleLabel')}>
      {tabs.map((tab, index) => <button key={tab} type="button" role="tab"
        ref={node => { buttons.current[index] = node; }}
        id={`people-tab-${tab}`} aria-controls={`people-panel-${tab}`}
        aria-selected={active === tab} tabIndex={active === tab ? 0 : -1}
        onClick={() => select(tab)} onKeyDown={event => onKeyDown(event, index)}
        data-testid={`people-tab-${tab}`}><span className="organizer-people__tab-icon" aria-hidden="true">{tab === 'list' ? <UsersIcon /> : <PaperPlaneIcon />}</span>{t(`organizerPanel.${tab}`)}</button>)}
    </div>
    <section key={active} role="tabpanel"
      id={`people-panel-${active}`} aria-labelledby={`people-tab-${active}`} tabIndex={0}>
      {active === 'list' ? <>
        <OrganizerPanelActivity info={props.info} cards={props.cards} />
        {tableSlot ? createPortal(table, tableSlot) : mobile && dockedTop !== null ? <DockedSheet top={dockedTop} animateEntrance={animateDocked}
          title={`${t('box.organizer.filters.title')} (${props.info.participantsNumber})`} onExpand={() => openTable(true)}>
          <div className="participants-table-sheet">{table}</div>
        </DockedSheet> : !mobile && <div className="participants-inline-slot"><Panel className="organizer-people__content" elevated>{table}</Panel></div>}
      </> : <Panel padding="none" elevated className="organizer-people__content">{table}</Panel>}
    </section>
  </div>;
}
